// HandoffLineField.tsx · v1.2 · v12.10.6 (#1150 a proof with no keys recorded carries over) · v1.1 · v12.10.5.1 (#1135/#1136 typing + Backspace through the mask · #1138 Verify saves first (the page decides) · #1139 Cancel, Answered · #1140 keys are part of Verified) · v1.0 · v12.10.5 (#1122 #1128 #1131 #1132, UI-LOCK-12.10.5 v1.3) · 2026-10-03 · VoiceSpectrum AI
//
// One Handoff line, top to bottom, exactly as locked 10/3:
//   1. the number box (§38 mask) with the pill INSIDE it on the right - ✓ Verified / Not verified / Calling…
//   2. "After it answers, press" - digits, * # and commas; hint "Comma = 1 second pause" (#1132)
//   3. Verify, with two lines beside it: "Last verification" / "+1 (908) 688-8810 · 3 days ago" (exact time on hover);
//      never verified: "Never - calls cannot reach this line yet"; a failed try shows its reason in red under it (#1131)
// Verified = answered and pressed 1, and the number verified is the number in the box now. Verify is available any
// time; it calls the SAVED number, so an edited number is saved first.
import { maskPhone, phoneFromInput, phoneError, toE164, agoWords, exactEt } from '../../lib/phone';
import { HelpTooltip } from './HelpTooltip';

export interface LineCheck {
  verified: boolean; verifiedAt?: string | null; verifiedNumber?: string | null;
  verifiedKeys?: string | null;   // v12.10.5.1 (#1140)
  calling: boolean; answered?: boolean; lastTryAt?: string | null; lastTryResult?: string | null;
}

const KEYS_RX = /^[0-9*#,]*$/;

/** v12.10.5.1 (#1140, Tushar 10/3): Verified = the number AND the keys on screen are the ones that were verified. */
export function lineIsVerified(number: string | null | undefined, keys: string | null | undefined, check: LineCheck | undefined): boolean {
  const n = toE164(number);
  return !!n && !!check?.verifiedAt && toE164(check.verifiedNumber) === n
    // v12.10.6 (#1150): no keys recorded = a proof from before 12.10.5.1 - it carries over (the API stamps the keys before they change)
    && (check.verifiedKeys == null || check.verifiedKeys.trim() === (keys ?? '').trim());
}

export function HandoffLineField({ label, number, keys, check, disabled, verifying, onNumber, onKeys, onVerify, onCancel }: {
  label: string;
  number: string | null | undefined;
  keys: string | null | undefined;
  check: LineCheck | undefined;
  disabled?: boolean;
  verifying?: boolean;
  onNumber: (v: string) => void;
  onKeys: (v: string) => void;
  onVerify: () => void;
  onCancel: () => void;   // v12.10.5.1 (#1139)
}) {
  const err = phoneError(number);
  const verified = lineIsVerified(number, keys, check);
  const calling = !!check?.calling || !!verifying;
  const failed = !verified && !!check?.lastTryResult;
  const answered = !!check?.answered;
  const pill = calling ? <span className="hl-pill run">{answered ? 'Answered…' : 'Calling…'}</span>
    : verified ? <span className="hl-pill ok">✓ Verified</span>
    : <span className={`hl-pill ${failed ? 'bad' : 'no'}`}>Not verified</span>;
  const keysBad = !KEYS_RX.test(keys ?? '');
  const hasNum = !!toE164(number);
  // v12.10.5.1 (#1138): Verify is offered whenever the box holds a valid number - an unsaved change is saved by the page first.
  const verifyWhy = !hasNum ? 'Type the number first' : keysBad ? 'Fix the keys first' : '';

  const last = calling
    ? <div>{answered ? 'Answered - waiting for 1' : `Calling ${maskPhone(toE164(number) ?? number)} - answer it and press 1`}</div>
    : (
      <>
        {check?.verifiedAt && check.verifiedNumber
          ? <div><span className="hl-stamp" title={exactEt(check.verifiedAt)}>{maskPhone(check.verifiedNumber)} · {agoWords(check.verifiedAt)}</span></div>
          : !check?.lastTryResult && <div>Never - calls cannot reach this line yet</div>}
        {check?.lastTryResult && (
          <div className="hl-fail" title={check.lastTryAt ? exactEt(check.lastTryAt) : undefined}>
            {check.verifiedAt ? 'This try: ' : ''}{check.lastTryResult}{check.lastTryAt ? ` · ${agoWords(check.lastTryAt)}` : ''}
          </div>
        )}
      </>
    );

  return (
    <div className="hl">
      <div className="aa-f">
        <label>{label}</label>
        <div className="hl-num">
          <input value={maskPhone(number)} inputMode="tel" autoComplete="off" disabled={disabled}
            onChange={(e) => onNumber(phoneFromInput(e.target.value, number))} aria-invalid={!!err} />
          {pill}
        </div>
        {err && <div className="hl-err">{err}</div>}
      </div>
      <div className="aa-f hl-keys">
        <label>After it answers, press
          <HelpTooltip sections={[
            ['What it does', 'Keys pressed once the line answers - an extension or a phone-menu path. Each comma waits 1 second.'],
            ['When it fires', 'On every transfer to this line, and on Verify - Verify walks the same path.'],
            ['Example', '1234 · 2 · 2,,1234#'],
          ]} />
        </label>
        <input value={keys ?? ''} placeholder="optional - e.g. 2,,1234#" maxLength={40} disabled={disabled}
          onChange={(e) => onKeys(e.target.value.replace(/\s/g, ''))} aria-invalid={keysBad} />
        {keysBad ? <div className="hl-err">Only digits, * , # and commas</div> : <div className="hl-hint">Comma = 1 second pause</div>}
      </div>
      <div className="hl-verify">
        {calling
          ? <button type="button" className="btn-soft" onClick={onCancel} disabled={!check?.calling} title="Hang up the test call">Cancel</button>
          : <button type="button" className="btn-soft" onClick={onVerify} disabled={disabled || !!verifyWhy} title={verifyWhy || undefined}>Verify</button>}
        <div className="hl-last">
          <div className="hl-last-h">Last verification</div>
          {last}
        </div>
      </div>
    </div>
  );
}
